import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Histogram } from '../../types/stats';
import { HistogramBarChart, EvaluationSelection } from './HistogramBarChart';

interface StartDistanceChartsProps {
  histogram: Histogram;
  evaluations: EvaluationSelection;
}

export const StartDistanceCharts = ({ histogram, evaluations }: StartDistanceChartsProps) => {
  const radialOption = useMemo(() => {
    const maxValue =
      histogram.circumference ??
      (histogram.bins.reduce((max, bin) => Math.max(max, bin.value), 0) || 360);
    const segments: Record<string, number[]> = {};
    const bins = histogram.bins;
    const evalKeys = Object.keys(evaluations) as Array<keyof EvaluationSelection>;

    evalKeys.forEach((evaluation) => {
      if (!evaluations[evaluation]) {
        return;
      }
      bins.forEach((bin, index) => {
        const comments = bin.counts[evaluation];
        Object.entries(comments).forEach(([comment, count]) => {
          const key = `${evaluation} • ${comment}`;
          if (!segments[key]) {
            segments[key] = Array(bins.length).fill(0);
          }
          segments[key][index] = count;
        });
      });
    });

    const totalSeries = bins.map((bin, index) => {
      const total = evalKeys.reduce((sum, evaluation) => {
        if (!evaluations[evaluation]) {
          return sum;
        }
        const counts = bin.counts[evaluation];
        return sum + Object.values(counts).reduce((inner, value) => inner + value, 0);
      }, 0);
      return { angle: bin.value, value: total };
    });

    const legend = Object.keys(segments);
    const defectSeries: echarts.SeriesOption[] = legend.map((name) => ({
      name,
      type: 'line',
      coordinateSystem: 'polar',
      data: segments[name].map((value, idx) => [value, bins[idx].value]),
      smooth: true,
      lineStyle: { width: 1 },
      symbol: 'none',
      emphasis: { focus: 'series' },
    }));

    return {
      tooltip: {
        trigger: 'item',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => `${params.value[1]} mm<br/>${params.value[0]} indications`,
      },
      angleAxis: {
        type: 'value',
        min: 0,
        max: maxValue,
        startAngle: 90,
        name: histogram.column,
        nameLocation: 'middle',
        nameGap: 50,
      },
      radiusAxis: { type: 'value', name: 'Number of indications' },
      polar: {},
      legend: { data: ['Total', ...legend], type: 'scroll' },
      dataZoom: [
        {
          type: 'inside',
          polarIndex: 0,
        },
        {
          type: 'slider',
          orient: 'vertical',
          right: 10,
          top: 40,
          bottom: 40,
        },
      ],
      series: [
        {
          type: 'bar',
          coordinateSystem: 'polar',
          data: totalSeries.map((item) => [item.value, item.angle]),
          itemStyle: { color: '#22c55e' },
          name: 'Total',
        },
        ...defectSeries,
      ],
    } as echarts.EChartsOption;
  }, [histogram, evaluations]);

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Radial coverage</h3>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Total indications</p>
        </div>
        <ReactECharts option={radialOption} style={{ height: 420 }} />
      </div>
      <HistogramBarChart histogram={histogram} evaluations={evaluations} title="Start distance breakdown" />
    </div>
  );
};
